import React, { useState } from "react"
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import {
    Crown,
    Info,
    Rocket,
    Sparkles,
    TrendingUp,
    X,
    Loader2
} from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import { useEffect } from "react"
import services from "../../../API"
import { toastify } from "../../Toast"

function cn(...inputs) {
    return twMerge(clsx(inputs))
}

const PLAN_UI_METADATA = {
    starter: {
        identityLabel: "You're just getting started",
        subtext: "Launch your first ads and content without guessing what to make.",
        outcomeText: "Create your first video ads and images",
        cta: "Start getting users",
        icon: Rocket,
    },
    growth: {
        identityLabel: "Things are starting to work",
        subtext: "Turn what's working into more ads and real growth.",
        outcomeText: "Create and test more ads, faster",
        starterHint: "Most builders start here",
        cta: "Scale what's working",
        highlighted: true,
        badge: "Most builders choose this",
        icon: TrendingUp,
    },
    pro: {
        identityLabel: "You're ready to scale",
        subtext: "Scale winning ads across channels, without slowing down.",
        outcomeText: "Run more ads and scale what works",
        cta: "Scale everything",
        icon: Crown,
    },
}

export default function PricePlansPopup({
    open: controlledOpen,
    onOpenChange,
    triggerLabel = "View pricing plans",
    triggerClassName,
    showTrigger = true,
    currentPlanName = "",
    currentStripePriceId = "",
    handleRefresh = () => { },
    isComplementary = false,
}) {
    const [upgradingStripePriceId, setUpgradingStripePriceId] = useState(null)
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const isControlled = controlledOpen !== undefined
    const open = isControlled ? controlledOpen : uncontrolledOpen

    useDispatch();
    const plansData = useSelector((state) => state.data.apiData.plansList)
    const isFetchingPlans = useSelector((state) => state.data.loading.plansList)
    const isUpdatingSubscription = useSelector((state) => state.data.loading.activateOrUpdateSubscription)


    useEffect(() => {
        if (open && !plansData && !isFetchingPlans) {
            services.getPlansList()
        }
    }, [open, plansData, isFetchingPlans])

    const handleUpgrade = async (stripePriceId) => {
        if (!stripePriceId) {
            console.warn("No stripePriceId found for this plan.")
            toastify("error", "Plan details not found. Please try again later or contact support.")
            return
        }

        setUpgradingStripePriceId(stripePriceId)
        try {
            const response = await services.activateOrUpdateSubscription({ stripePriceId })

            // Handle Stripe Checkout redirect if URL is provided
            if (response?.data?.type === 'redirect' && response?.data?.url) {
                window.open(response.data.url, '_blank')
            }

            handleRefresh()
            handleOpenChange(false)
        } catch (err) {
            console.error("Upgrade failed:", err)
        } finally {
            setUpgradingStripePriceId(null)
        }
    }

    const handleOpenChange = (newOpen) => {
        if (!isControlled) {
            setUncontrolledOpen(newOpen)
        }
        if (onOpenChange) {
            onOpenChange(newOpen)
        }
    }

    return (
        <>
            {showTrigger ? (
                <button
                    type="button"
                    onClick={() => handleOpenChange(true)}
                    className={cn(
                        "inline-flex items-center justify-center rounded-full bg-[#FF6600] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#E55A00]",
                        triggerClassName
                    )}
                >
                    {triggerLabel}
                </button>
            ) : null}

            {open ? (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    <div
                        className="fixed inset-0 bg-black/50 dark:bg-black/70 transition-opacity"
                        onClick={() => handleOpenChange(false)}
                    />

                    <div
                        className="relative w-full max-w-6xl rounded-[28px] bg-white dark:bg-[#111111] p-0 shadow-2xl z-10 animate-in fade-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >

                        <button
                            className="absolute right-4 top-4 md:right-6 md:top-6 z-50 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:text-slate-900 dark:border-gray-800 dark:bg-[#1A1A1A] dark:text-gray-400 dark:hover:text-white shadow-sm"
                            onClick={() => handleOpenChange(false)}
                        >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Close pricing popup</span>
                        </button>

                        <div className="relative overflow-hidden rounded-[28px]">
                            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#FFF4EC] to-transparent pointer-events-none dark:opacity-10" />

                            <div className="relative px-6 pb-8 pt-20 md:px-8 md:pb-10 md:pt-24">

                                <div className="grid items-stretch gap-6 md:grid-cols-3">
                                    {isComplementary && (
                                        <div className="col-span-3 mb-8 text-center rounded-3xl bg-orange-50/50 p-8 border border-orange-200 dark:bg-orange-500/5 dark:border-orange-500/10">
                                            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Complementary Account</h2>
                                            <p className="text-slate-500 dark:text-gray-400 max-w-2xl mx-auto">
                                                You are currently on a complementary account. All premium plans are available for purchase if you wish to upgrade your features.
                                            </p>
                                        </div>
                                    )}
                                    {isFetchingPlans && !plansData ? (
                                        <div className="col-span-3 flex h-64 flex-col items-center justify-center gap-4">
                                            <Loader2 className="h-8 w-8 animate-spin text-[#FF6600]" />
                                            <p className="text-slate-500 dark:text-gray-400">Loading pricing plans...</p>
                                        </div>
                                    ) : (plansData?.data?.plans || (Array.isArray(plansData) ? plansData : [])).map((apiPlan) => {
                                        const planNameLower = (apiPlan.name || "").toLowerCase();
                                        const planKey = Object.keys(PLAN_UI_METADATA).find(key => 
                                            planNameLower.includes(key)
                                        ) || "starter";

                                        const uiData = PLAN_UI_METADATA[planKey] || PLAN_UI_METADATA.starter;
                                        const Icon = uiData.icon || Sparkles;

                                        const isCurrentPlan = !isComplementary && (
                                            (apiPlan.stripePriceId && currentStripePriceId && apiPlan.stripePriceId === currentStripePriceId) ||
                                            (currentPlanName && apiPlan.name && (
                                                currentPlanName.toLowerCase().includes(apiPlan.name.toLowerCase()) ||
                                                apiPlan.name.toLowerCase().includes(currentPlanName.toLowerCase())
                                            ))
                                        );

                                        const showDefaultHighlight = uiData.highlighted && !currentPlanName;
                                        const isThisPlanLoading = isUpdatingSubscription && upgradingStripePriceId === apiPlan.stripePriceId;

                                        return (
                                            <div
                                                key={apiPlan.stripePriceId || apiPlan.name}
                                                className={cn(
                                                    "relative flex flex-col rounded-3xl p-6 transition-all duration-200 hover:-translate-y-1",
                                                    isCurrentPlan
                                                        ? "border-2 border-[#FF6600] bg-[#FFFCFA] shadow-[0_10px_40px_rgba(255,102,0,0.12)] dark:bg-[#1A1A1A] dark:border-[#FF6600] dark:shadow-[0_10px_40px_rgba(255,102,0,0.15)]"
                                                        : showDefaultHighlight
                                                            ? "border-2 border-[#FF6600]/25 bg-[#FFFCFA] shadow-[0_10px_40px_rgba(255,102,0,0.12)] dark:bg-[#1A1A1A] dark:border-[#FF6600]/40 dark:shadow-[0_10px_40px_rgba(255,102,0,0.15)]"
                                                            : "border border-slate-200 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)] dark:border-gray-800 dark:bg-[#1A1A1A] dark:shadow-none"
                                                )}
                                            >
                                                {isCurrentPlan ? (
                                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                        <span className="whitespace-nowrap rounded-full bg-[#FF6600] px-4 py-1 text-[11px] font-bold text-white">
                                                            Current Plan
                                                        </span>
                                                    </div>
                                                ) : (uiData.badge && showDefaultHighlight) ? (
                                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                        <span className="whitespace-nowrap rounded-full bg-[#FF6600] px-4 py-1 text-[11px] font-bold text-white">
                                                            {uiData.badge}
                                                        </span>
                                                    </div>
                                                ) : null}

                                                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 dark:bg-[#232323] dark:text-gray-300">
                                                    <Icon className="h-5 w-5" />
                                                </div>

                                                <div className="flex-1">
                                                    <p className="mb-1 text-xs text-slate-500 dark:text-gray-400">{uiData.identityLabel}</p>
                                                    <h3 className="mb-3 text-lg font-bold text-slate-900 dark:text-white">{apiPlan.displayName || apiPlan.name}</h3>

                                                    <div className="mb-3">
                                                        <span className="text-[2.75rem] font-extrabold tracking-tight text-slate-900 dark:text-white">
                                                            ${apiPlan.price}
                                                        </span>
                                                        <span className="ml-1 text-sm text-slate-400 dark:text-gray-500">/ {apiPlan.interval || 'month'}</span>
                                                    </div>

                                                    <div className="mb-4 min-h-[72px]">
                                                        <p className="text-sm leading-relaxed text-slate-500 dark:text-gray-400">{uiData.subtext}</p>
                                                    </div>

                                                    <div className="mb-2 flex items-center justify-between rounded-2xl bg-slate-100 px-4 py-3 dark:bg-[#232323]">
                                                        <span className="text-sm font-semibold text-slate-700 dark:text-gray-300">
                                                            {(apiPlan.allowedCredits || 0).toLocaleString()} credits
                                                        </span>

                                                        <div className="group relative flex items-center">
                                                            <button
                                                                type="button"
                                                                className="text-slate-400 transition hover:text-slate-600 dark:hover:text-gray-300"
                                                                aria-label={`Credit information for ${apiPlan.name}`}
                                                            >
                                                                <Info className="h-4 w-4" />
                                                            </button>
                                                            <div className="pointer-events-none absolute bottom-[120%] left-1/2 z-[60] -translate-x-1/2 w-max max-w-[260px] opacity-0 transition-opacity group-hover:opacity-100 rounded-xl bg-slate-900 px-4 py-3 text-[13px] leading-snug text-white shadow-xl dark:bg-[#111111] dark:border dark:border-gray-800">
                                                                Credits are only used when you create something. Videos use more than images.
                                                                <div className="absolute left-1/2 -bottom-2 -translate-x-1/2 border-solid border-t-slate-900 border-t-8 border-x-transparent border-x-8 border-b-0 dark:border-t-[#111111]" />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <p className="mb-2 text-xs text-slate-500 dark:text-gray-400">{uiData.outcomeText}</p>

                                                    {uiData.starterHint ? (
                                                        <p className="mb-3 text-[11px] text-slate-400 dark:text-gray-500">{uiData.starterHint}</p>
                                                    ) : null}
                                                </div>

                                                <button
                                                    type="button"
                                                    disabled={isCurrentPlan || isUpdatingSubscription}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleUpgrade(apiPlan.stripePriceId);
                                                    }}
                                                    className={cn(
                                                        "mt-auto w-full rounded-xl px-4 py-3 text-sm font-semibold transition flex items-center justify-center gap-2",
                                                        isCurrentPlan
                                                            ? "bg-[#FF6600] text-white opacity-50 cursor-not-allowed"
                                                            : showDefaultHighlight
                                                                ? "bg-[#FF6600] text-white hover:bg-[#E55A00]"
                                                                : "border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 dark:border-gray-700 dark:bg-[#1A1A1A] dark:text-white dark:hover:bg-[#232323]",
                                                        isUpdatingSubscription && "opacity-70 cursor-not-allowed"
                                                    )}
                                                >
                                                    {isThisPlanLoading ? (
                                                        <>
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                            Processing...
                                                        </>
                                                    ) : isCurrentPlan ? (
                                                        "Current Plan"
                                                    ) : (
                                                        uiData.cta || "Upgrade"
                                                    )}
                                                </button>

                                                {uiData.riskText ? (
                                                    <p className="mt-2 text-center text-[12px] text-slate-400 dark:text-gray-500">
                                                        {uiData.riskText}
                                                    </p>
                                                ) : null}
                                            </div>
                                        )
                                    })}
                                </div>


                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    )
}
